import { useRef, useState, useEffect } from "react";
import { Col, Row, Button, Input, Select, DatePicker, message } from "antd";
import CustomButton from "@/components/CustomButton";
import { CloseOutlined, LoadingOutlined } from "@ant-design/icons";
import { useHistory, useParams, useRouteMatch } from "react-router-dom";

import CountryPhoneInput, { ConfigProvider } from "antd-country-phone-input";
import en from "../../constants/world.json";

import moment from "moment";

import "./createCompany.less";
import ImgUpload from "@assets/images/img-upload.png";
import { disabledDates, generateArrayOfYears, showSuccessPopup } from "@utils";
import { useTypedSelector } from "@hooks";
import {
  ErrorServices,
  useFetchCompanyQuery,
  useFetchCurrenciesQuery,
  useUpdateCompanyMutation,
  useUploadCompanyImageMutation,
} from "@services";
import { ICurrency } from "@types";
import { paths } from "@router";
import customErrorMessage from "@/components/CustomErrorMessage";

const { Option } = Select;

function beforeUpload(file: any) {
  const isJpgOrPng = file.type === "image/jpeg" || file.type === "image/png";
  if (!isJpgOrPng) {
    customErrorMessage("You can only upload JPG/PNG file!");
  }
  const isLt2M = file.size / 1024 / 1024 < 2;
  if (!isLt2M) {
    customErrorMessage("Image must be smaller than 2MB!");
  }
  return isJpgOrPng && isLt2M;
}

interface IFormValues {
  country_id: number | null;
  currency_id: number | null;
  name: string;
  address: string;
  postal_code: string;
  country_headquarter: string;
  financial_year: number | null;
  contract_start_date: string;
  contract_end_date: string;
  large_logo_url: string;
  small_logo_url: string;
  stock_data: any[];

  // stock_tracking_ids: string[];
  person_name: string;
  person_phone_code: number | null;
  person_phone_number: string;
  person_email: string;
  person_country_id: number | null;
  application_access: boolean;
}

const INITIAL_VALUES: IFormValues = {
  country_id: null,
  currency_id: null,
  name: "",
  address: "",
  postal_code: "",
  country_headquarter: "",
  financial_year: null,
  contract_end_date: "",
  contract_start_date: "",
  small_logo_url:
    "https://cdn.sanity.io/images/92ui5egz/production/7c1c60e9afaaaa3cce61e5101717796d21b7f914-1426x1080.svg?auto=format",
  large_logo_url:
    "https://cdn.sanity.io/images/92ui5egz/production/7c1c60e9afaaaa3cce61e5101717796d21b7f914-1426x1080.svg?auto=format",
  // stock_tracking_ids: [],
  stock_data: [],
  person_name: "",
  person_phone_code: null,
  person_phone_number: "",
  person_email: "",
  person_country_id: null,
  application_access: false,
};

type IFormKeys = keyof IFormValues;

const validateValues = (data: IFormValues) => {
  let cond = false;
  for (var key in data) {
    if (data[key as IFormKeys] === "" || data[key as IFormKeys] === null) {
      cond = true;
    }
  }
  return cond;
};

const ClientCreateCompany = () => {
  const history = useHistory();
  const inputRef = useRef<any>();
  const { company_id } = useParams<{ company_id: string }>();
  const [largeLogo, setLargeLogo] = useState("");
  const [loadingLargeLogo, setLoadingLargeLogo] = useState(false);
  const { path } = useRouteMatch<{ path: string }>();
  const [stockId, setStockId] = useState("");
  const [status, setStatus] = useState("ACTIVE");
  const [isDisabled, setIsDisabled] = useState(true);
  const { countries } = useTypedSelector(state => state.countries);
  const { data: currenciesData, isLoading: isFetchingCurrencies } =
    useFetchCurrenciesQuery(null);
  const { data: currencies } = currenciesData || {};
  const [company, setCompany] = useState(INITIAL_VALUES);
  const isEdit = path === paths.admin.users.companies.edit;
  const [uploadCompanyImage] = useUploadCompanyImageMutation();
  const { data: companyData } = useFetchCompanyQuery({ company_id });
  const { data: selectedCompany } = companyData || {};
  const [updateCompany, { isLoading: isUpdating }] = useUpdateCompanyMutation();

  function disabledDate(current: any) {
    return disabledDates(
      current,
      moment(moment(company?.contract_start_date)).endOf("day")
    );
  }

  useEffect(() => {
    if (isEdit && selectedCompany) {
      const { country, currency, user, person_country, stock_data } =
        selectedCompany;
      const company = {
        ...selectedCompany,
        country_id: country?.id,
        currency_id: currency?.id,
        person_country_id: person_country?.id,
        person_email: user?.email,
        person_name: user?.first_name + user?.last_name,
        person_phone_code: user?.phone_code,
        person_phone_number: user?.phone_number,
        stock_data,
      };
      delete company?.country;
      delete company?.currency;
      delete company?.user;
      delete company?.person_country;
      delete company?.applications;
      setStatus(company?.status);
      setLargeLogo(selectedCompany?.large_logo_url);
      setCompany(company);
    }
  }, [selectedCompany, path, isEdit]);

  const handleImage = async (event: any) => {
    setLoadingLargeLogo(true);
    const file = event.target.files[0];
    const isCond = beforeUpload(file);
    if (isCond) {
      try {
        const formData = new FormData();
        formData.append("file", file);
        const res = await uploadCompanyImage({
          body: formData,
        }).unwrap();
        setLargeLogo(res?.data?.url);
        message.success(res?.message);
      } catch (err: any) {
        customErrorMessage(err?.message);
        console.log(err);
      }
    }
  };

  const onChange = (event: any) => {
    setCompany(prev => ({
      ...prev,
      [event.target.name]: event.target.value,
    }));
  };

  const onSubmit = async () => {
    try {
      await editCompany({ ...company, large_logo_url: largeLogo, status });
      history.push(
        paths.admin.users.companies.profile.details.replace(
          ":company_id",
          company_id
        )
      );
      showSuccessPopup({
        title: "Company Updated",
        desc: `You have successfully updated the company.`,
      });
    } catch (err: any) {
      ErrorServices(err);
      console.log(err);
    }
  };

  const editCompany = async (body: any) =>
    await updateCompany({ body, company_id }).unwrap();

  return (
    <div className="container">
      <Row>
        <Col span={24}>
          <div className="main-heading mb-6">Update the Company</div>
        </Col>
      </Row>
      <div className="addCompany">
        <Row className="addCompany__header">
          <div className="sub-heading">Basic information</div>
        </Row>
        <div className="addCompany__content">
          <Row className="addCompany__row" justify="space-between">
            <Col span={9}>
              <Row>
                <label>Company Name</label>
                <Input
                  onChange={onChange}
                  name="name"
                  value={company?.name}
                  size="large"
                  type="text"
                  placeholder="Enter company name here..."
                />
              </Row>
              <Row>
                <label className="mt-12">Address</label>
                <Input
                  onChange={onChange}
                  value={company?.address}
                  name="address"
                  size="large"
                  type="text"
                  placeholder="Enter company address here..."
                />
              </Row>
              <Row>
                <label className="mt-12">Postal Code</label>
                <Input
                  value={company?.postal_code}
                  onChange={onChange}
                  name="postal_code"
                  size="large"
                  type="text"
                  placeholder="Enter postal code here..."
                />
              </Row>
              <label className="mt-12">Status</label>
              <Select
                disabled={!selectedCompany}
                onChange={status => setStatus(status)}
                value={status}
                showArrow={true}
                size="large"
                className="mb-12"
                placeholder="Select status from here..."
              >
                {[
                  { value: "IN_PROGRESS", label: "In Progress" },
                  { value: "ACTIVE", label: "Active" },
                  { value: "EXPIRED", label: "Expired" },
                ]?.map(({ value, label }) => (
                  <Option key={value} value={value}>
                    {label}
                  </Option>
                ))}
              </Select>
            </Col>
            <Col className="addCompany__col2" span={9}>
              <input
                id="myInput"
                type="file"
                ref={inputRef}
                hidden={true}
                accept="image/png, image/jpeg"
                onChange={handleImage}
              />
              <div onClick={() => inputRef?.current?.click()}>
                <div className="addCompany__col2__image">
                  {loadingLargeLogo && <LoadingOutlined className="spinner" />}

                  <img
                    className={`${
                      loadingLargeLogo ? "addCompany__col2__image--hidden" : ""
                    }`}
                    onLoadCapture={() => {
                      setLoadingLargeLogo(false);
                    }}
                    src={largeLogo || ImgUpload}
                    alt="avatar"
                  />
                </div>
                <p className="img_description_text">UPLOAD LARGE LOGO</p>
                <p className="img_description_size">1000 x 1000</p>
              </div>
            </Col>
          </Row>
          <div className="mt-24 mb-12 sub-heading">Corporate Information</div>
          <Row className="addCompany__row" justify="space-between">
            <Col span={9}>
              <Row>
                <label>Country</label>
                <Select
                  onChange={country_id => {
                    setCompany(prev => ({
                      ...prev,
                      country_id,
                    }));
                  }}
                  value={company?.country_id || undefined}
                  showArrow={true}
                  size="large"
                  showSearch={true}
                  filterOption={(input: any, option: any) =>
                    option.children
                      .toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                  }
                  placeholder="Select country from here..."
                >
                  {countries?.map(({ id, name }) => (
                    <Option key={id} value={id}>
                      {name}
                    </Option>
                  ))}
                </Select>
              </Row>
              <Row>
                <label className="mt-12">Headquarter Location</label>
                <Input
                  value={company?.country_headquarter}
                  name="country_headquarter"
                  onChange={onChange}
                  className="form__input"
                  type="text"
                  placeholder="Enter location of headquater"
                />
              </Row>
              <Row>
                <label className="mt-12">Currency</label>
                <Select
                  value={company?.currency_id || undefined}
                  loading={isFetchingCurrencies}
                  onChange={currency_id =>
                    setCompany(prev => ({
                      ...prev,
                      currency_id,
                    }))
                  }
                  showArrow={true}
                  showSearch={true}
                  size="large"
                  filterOption={(input: any, option: any) =>
                    option.children
                      .toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                  }
                  placeholder="Select currency from here..."
                >
                  {currencies?.map(({ id, name, code }: ICurrency) => (
                    <Option key={id} value={id}>
                      {`${name} (${code})`}
                    </Option>
                  ))}
                </Select>
              </Row>
              <Row>
                <label className="mt-12">Financial Year</label>
                <Select
                  onChange={financial_year =>
                    setCompany(prev => ({ ...prev, financial_year }))
                  }
                  value={company?.financial_year || undefined}
                  filterOption={(input: any, option: any) =>
                    option.children
                      .toString()
                      .toLowerCase()
                      .indexOf(input.toString().toLowerCase()) >= 0
                  }
                  showSearch={true}
                  size="large"
                  placeholder="Select financial year from here..."
                >
                  {generateArrayOfYears(50)?.map((year: number) => (
                    <Option key={year.toString()} value={year.toString()}>
                      {year}
                    </Option>
                  ))}
                </Select>
              </Row>
              {/* <Row justify="space-between">
                <label className="mt-12">Stock tracking ID</label>
                <div
                  className={`${
                    !company?.stock_tracking_ids?.length && "mb-12"
                  } addCompany__stockIds`}
                >
                  <Input
                    size="large"
                    onChange={e => setStockId(e.target.value)}
                    value={stockId}
                    type="text"
                    placeholder="Enter stock ID here..."
                  />
                  <Button
                    type="primary"
                    disabled={!stockId}
                    size="large"
                    onClick={() => {
                      setCompany(prev => ({
                        ...prev,
                        stock_tracking_ids: [
                          ...company?.stock_tracking_ids,
                          stockId,
                        ],
                      }));
                      setStockId("");
                      message.success("Stock tracking id added!");
                    }}
                  >
                    +
                  </Button>
                </div>
                {company?.stock_tracking_ids?.length ? (
                  <div className="addCompany__stockIds__list mb-12">
                    {company?.stock_tracking_ids?.map((id, idx) => (
                      <div key={idx} className="addCompany__stockIds__list__id">
                        <span className="addCompany__stockIds__list__id__txt">
                          {id}
                        </span>
                        <CloseOutlined
                          onClick={() =>
                            setCompany(prev => ({
                              ...prev,
                              stock_tracking_ids:
                                company?.stock_tracking_ids?.filter(
                                  x => x !== id
                                ),
                            }))
                          }
                        />
                      </div>
                    ))}
                  </div>
                ) : null}
              </Row> */}
            </Col>
            <Col className="addCompany__col2" span={9} />
          </Row>

          <Row>
            <Col span={23}>
              <Row>
                <div className="flex  gap-2 flex-wrap  justify-start items-center mt-12">
                  {company?.stock_data && company?.stock_data.length > 0
                    ? company?.stock_data?.map((item, i) => {
                        return (
                          <div key={i} className="bg-gray-100 p-4 rounded-md">
                            <div className="flex justify-end items-end">
                              <CloseOutlined
                                className="right-0 cursor-pointer"
                                onClick={() => {
                                  setCompany(prev => ({
                                    ...prev,
                                    stock_data: prev.stock_data.filter(
                                      x => x.tracking_id !== item?.tracking_id
                                    ),
                                  }));
                                }}
                              />
                            </div>

                            <div>
                              <label className="">Stock Tracking ID</label>
                              <Input
                                size="large"
                                value={item?.tracking_id}
                                onChange={e => {
                                  const updatedTrackingId = e.target.value;
                                  setCompany(prev => ({
                                    ...prev,
                                    stock_data: prev.stock_data.map((x, idx) =>
                                      idx === i
                                        ? {
                                            ...x,
                                            tracking_id: updatedTrackingId,
                                          }
                                        : x
                                    ),
                                  }));
                                }}
                                type="text"
                                placeholder="Enter stock tracking ID here..."
                              />
                            </div>
                            <div>
                              <label className="">Stock Type</label>
                              <Select
                                onChange={stockType =>
                                  setCompany(prev => ({
                                    ...prev,
                                    stock_data: prev.stock_data.map(
                                      (item, idx) =>
                                        idx === i
                                          ? { ...item, stock_type: stockType }
                                          : item
                                    ),
                                  }))
                                }
                                value={item?.stock_type || undefined}
                                showSearch={true}
                                size="large"
                                placeholder="Select stock type from here..."
                              >
                                <Option value="in-House">In-House</Option>
                                <Option value="international">
                                  International
                                </Option>
                              </Select>
                            </div>
                            <div>
                              <label className="mt-">Currency</label>
                              <Select
                                value={item?.stock_currency_id || undefined}
                                loading={isFetchingCurrencies}
                                onChange={currency_id =>
                                  setCompany(prev => ({
                                    ...prev,
                                    stock_data: prev.stock_data.map(
                                      (item, idx) =>
                                        idx === i
                                          ? {
                                              ...item,
                                              stock_currency_id: currency_id,
                                            }
                                          : item
                                    ),
                                  }))
                                }
                                showArrow={true}
                                showSearch={true}
                                size="large"
                                filterOption={(input: any, option: any) =>
                                  option.children
                                    .toLowerCase()
                                    .indexOf(input.toLowerCase()) >= 0
                                }
                                placeholder="Select currency from here..."
                              >
                                {/* {currencies?.map(
                                  ({ id, name, symbol }: ICurrency) => (
                                    <Option key={id} value={id}>
                                      {`${name} (${symbol})`}
                                    </Option>
                                  )
                                )} */}

                                {item?.stock_type === "international" ? (
                                  <Option key={138} value={138}>
                                    USD ($)
                                  </Option>
                                ) : (
                                  currencies?.map(
                                    ({ id, name, symbol }: ICurrency) => (
                                      <Option key={id} value={id}>
                                        {`${name} (${symbol})`}
                                      </Option>
                                    )
                                  )
                                )}
                              </Select>
                            </div>
                            <div>
                              <label className="">Stock Value</label>
                              <Input
                                size="large"
                                contentEditable={
                                  item?.stock_type != "international"
                                }
                                disabled={item?.stock_type === "international"}
                                value={item?.stock_value}
                                onChange={e => {
                                  const updatedStockValue = e.target.value;
                                  setCompany(prev => ({
                                    ...prev,
                                    stock_data: prev.stock_data.map((x, idx) =>
                                      idx === i
                                        ? {
                                            ...x,
                                            stock_value: updatedStockValue,
                                          }
                                        : x
                                    ),
                                  }));
                                }}
                                type="number"
                                placeholder="Enter stock value here..."
                              />
                            </div>
                          </div>
                        );
                      })
                    : null}

                  <div
                    className="bg-gray-100 p-4 rounded-md flex justify-center h-8 items-center cursor-pointer"
                    onClick={() => {
                      setCompany(prev => ({
                        ...prev,
                        stock_data: [
                          ...prev.stock_data,
                          {
                            tracking_id: "",
                            stock_type: "",
                            stock_value: "",
                          },
                        ],
                      }));
                    }}
                  >
                    <span className="text-sm font-bold">Add Stock</span>
                  </div>
                </div>
              </Row>
            </Col>
          </Row>
          <div className="mt-24 mb-12 sub-heading">Contract Details</div>
          <label>Contract Duration</label>
          <Row className="addCompany__row" justify="space-between">
            <Col span={12}>
              <Row justify="space-between">
                <Col span={11}>
                  <DatePicker
                    className="width-100"
                    value={
                      !company?.contract_start_date
                        ? undefined
                        : moment(company?.contract_start_date)
                    }
                    onChange={date => {
                      setCompany(prev => ({
                        ...prev,
                        contract_start_date: moment(date)
                          .format("YYYY-MM-DD HH:mm:ss")
                          ?.toString() as string,
                      }));
                      setIsDisabled(false);
                    }}
                    placeholder="Starting Date (DD/MM/YY)"
                  />
                </Col>
                <Col className="mb-12" span={11}>
                  <DatePicker
                    disabled={isDisabled}
                    disabledDate={disabledDate}
                    className="width-100"
                    value={
                      !company?.contract_end_date
                        ? undefined
                        : moment(company?.contract_end_date)
                    }
                    onChange={date =>
                      setCompany(prev => ({
                        ...prev,
                        contract_end_date: moment(date)
                          .format("YYYY-MM-DD HH:mm:ss")
                          ?.toString() as string,
                      }))
                    }
                    placeholder="Ending Date (DD/MM/YY)"
                  />
                </Col>
              </Row>
            </Col>
            <Col span={12}></Col>
          </Row>

          <div className="mt-24 mb-12 sub-heading">Contact Person Details</div>
          <Row className="addCompany__row" justify="space-between">
            <Col span={11}>
              <Row>
                <label>Contact Person</label>
                <Input
                  size="large"
                  name="person_name"
                  onChange={onChange}
                  value={company?.person_name}
                  placeholder="Enter name of contact person..."
                />
              </Row>
              <label className="mt-12"> Contact Number</label>
              <Row className="addCompany__number">
                <ConfigProvider locale={en}>
                  <CountryPhoneInput
                    placeholder="Enter contact number here..."
                    onChange={e => {
                      setCompany(prev => ({
                        ...prev,
                        person_phone_code: e.code as number,
                        person_phone_number: e.phone as string,
                      }));
                    }}
                    value={{
                      phone: company?.person_phone_number,
                      code: company?.person_phone_code || 92,
                    }}
                  />
                </ConfigProvider>
              </Row>
            </Col>
            <Col span={11}>
              <Row>
                <label>Contact Person's Country</label>
                <Select
                  onChange={person_country_id => {
                    setCompany(prev => ({
                      ...prev,
                      person_country_id,
                    }));
                  }}
                  value={company?.person_country_id || undefined}
                  showArrow={true}
                  showSearch={true}
                  size="large"
                  filterOption={(input: any, option: any) =>
                    option.children
                      .toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                  }
                  placeholder="Select country from here..."
                >
                  {countries?.map(({ id, name }) => (
                    <Option key={"" + id} value={id}>
                      {name}
                    </Option>
                  ))}
                </Select>
              </Row>
              <Row className="mt-12 mb-12">
                <label>Email Address</label>
                <Input
                  name="person_email"
                  value={company?.person_email}
                  onChange={onChange}
                  size="large"
                  placeholder="Enter email address here..."
                />
              </Row>
            </Col>
          </Row>
          <Row className="addCompany__btns">
            <Col span={24} className="flex items-center">
              <CustomButton
                // type="primary"
                btnName={
                  isUpdating ? (
                    <LoadingOutlined className="spinner" />
                  ) : (
                    "Update Company"
                  )
                }
                disabledProp={validateValues(company)}
                // size="large"
                onClick={onSubmit}
              >
                {isUpdating ? (
                  <LoadingOutlined className="spinner" />
                ) : (
                  "Update Company"
                )}
              </CustomButton>

              <CustomButton
                btnName={"Cancel"}
                // size="large"
                onClick={() =>
                  history.push(
                    paths.admin.users.companies.profile.details.replace(
                      ":company_id",
                      company_id
                    )
                  )
                }
              >
                Cancel
              </CustomButton>
            </Col>
          </Row>
        </div>
      </div>
    </div>
  );
};

export default ClientCreateCompany;
